import { Badge } from '@eventbrite/eds-badge';
import { gettext } from '@eventbrite/i18n';
import momentTz from 'moment-timezone';
import React from 'react';
import Countdown, { zeroPad } from 'react-countdown';

interface TimerPeriod {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
}

// NB: export for testing
export function getTimerMessage({
    days,
    hours,
    minutes,
    seconds,
    completed,
}: TimerPeriod) {
    const isEventStartingToday = !completed && days === 0;

    if (isEventStartingToday) {
        return `${gettext('Starts in %(hours)s:%(minutes)s:%(seconds)s', {
            hours: zeroPad(hours),
            minutes: zeroPad(minutes),
            seconds: zeroPad(seconds),
        })}`;
    } else if (completed) {
        return '';
    }
    const singularRemainingDays = gettext('Starts in %(days)s day', {
        days,
    });
    const pluralRemainingDays = gettext('Starts in %(days)s days', {
        days,
    });
    return days > 1 ? pluralRemainingDays : singularRemainingDays;
}

// export for testing
export const TimerRenderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
}: TimerPeriod) => {
    const timerMessage = getTimerMessage({
        days,
        hours,
        minutes,
        seconds,
        completed,
    });

    return <span data-spec="timer-message">{timerMessage}</span>;
};

interface Period {
    start: string;
    end: string;
}

export const Timer = ({ start, end }: Period) => {
    const isUpcoming = momentTz.utc(start).diff(momentTz.utc()) > 0;
    const hasEnded = momentTz.utc(momentTz.utc()).diff(end) > 0;

    let backgroundColor = 'primary-brand';
    let component = (
        <p data-spec="status-live">{gettext('Event Live - join here')}</p>
    );

    if (hasEnded) {
        backgroundColor = 'ui-500';

        component = (
            <p data-spec="status-ended">
                {gettext('Ended %(time_ago)s', {
                    time_ago: momentTz.utc(end).fromNow(),
                })}
            </p>
        );
    } else if (isUpcoming) {
        backgroundColor = 'ui-700';
        const dateTime = momentTz.utc(start).toString();
        component = (
            <span data-spec="component-countdown">
                <Countdown
                    date={dateTime}
                    daysInHours={true}
                    renderer={TimerRenderer}
                    zeroPadTime={2}
                />
            </span>
        );
    }

    return (
        <Badge
            data-spec="component-badge"
            text={component}
            bgColor={backgroundColor}
        ></Badge>
    );
};
